import i18n from 'i18next';

import Config from '../../Config/Config';
import { isSocialCasino } from '../../utils/casino';
import { getPageLanguage } from '../../utils/locale';

// Route Name: translated route key
const routes: Record<string, string> = {
    results: 'routes.results',
    'results.all': 'routes.results_all',
    rules: 'routes.rules',
    'rules.all': 'routes.rules_all',
    faq: 'routes.faq',
    singlenews: 'routes.news_single',
    news: 'routes.news',
    play: 'routes.play_single',
    'play.all': 'routes.play_all',
    howtoplay: 'routes.how_to_play',
    'howtoplay.all': 'routes.how_to_play_all',
    'blog.display': 'routes.blog',
    contact: 'routes.contact',
    casinoNews: 'routes.casinoNews',
    casinoNewsSingle: 'routes.casinoNewsSingle',
    checkout: 'routes.checkout',
    'lootbox.all': 'routes.lootbox_all',
    'lootbox.single': 'routes.lootbox_single',
    'auth.login': 'routes.auth.login',
    'auth.register': 'routes.auth.register',
};

export const pageLanguage = getLanguagePrefix(window.location.pathname) || getPageLanguage();

const templatesRoutes: Record<string, string> = {
    'account.deposit': isSocialCasino() ? 'account/buy-gc' : 'account/deposit',
    'account.loyalty.index': 'account/loyalty-program',
    'login.form': 'auth/login',
};

interface ParamsInterface {
    [key: string]: string | number | undefined;
}

export function Route(routeName: string, routeOptions: ParamsInterface = {}, prefix: string = ''): string {
    let route = routes[routeName];

    if (!route) {
        route = templatesRoutes[routeName];
    }

    if (!route) {
        return '';
    }

    route = i18n.t(route);
    route = transformRoute(route, routeOptions, routeName, prefix);

    return route;
}

function transformRoute(
    route: string,
    routeOptions: ParamsInterface = {},
    routeName: string,
    prefix: string = ''
): string {
    let transformedRoute = route;
    if (routeOptions.casino_slug) {
        /**
         * When casino is on subdomain or foreign subdomain,
         * we need to remove the casino prefix from the route
         */
        if (Config.get('casino.page_mode') !== 'page') {
            transformedRoute = transformedRoute.replace('{casino_slug}/', '');
        } else if (routeName === 'casinoNewsSingle') {
            transformedRoute = `${Config.get('casino.slug')}/${transformedRoute}`;
        }
    }

    // Replace route params
    transformedRoute = `/${prefix}${transformedRoute.replace(/\{([^}]+)\}/g, (_, param) =>
        String(routeOptions[param] ?? '')
    )}`;

    return localizePath(transformedRoute);
}

export function localizePath(path: string): string {
    let normalizedPath = path.startsWith('/') ? path : `/${path}`;

    if (pageLanguage && Config.isNot('app.locale', pageLanguage)) {
        normalizedPath = `/${pageLanguage}${normalizedPath}`;
    }

    return normalizedPath;
}

export function getLanguagePrefix(path: string): string {
    if (path === '' || path === '/') {
        return '';
    }

    const language = path.split('/').filter(Boolean)[0];
    if (!/^[a-z]{2}(-[A-Z]{2})?$/.test(language)) {
        return '';
    }

    return language;
}

export function addBackParameterToLink(link: string): string {
    // Use existing backUrl parameter if it exists, otherwise use current page URL.
    const backUrl = getBackUrlParameterFromWindow() || window.location.href;

    const url = new URL(link, window.location.origin);
    if (!url.searchParams.has('backUrl')) {
        url.searchParams.append('backUrl', backUrl);
    }

    return url.toString();
}

export function redirectOrReload(redirectUrl?: string): void {
    const targetUrl = redirectUrl || '/';

    try {
        const current = new URL(window.location.href);
        const target = new URL(targetUrl, window.location.origin);

        if (
            current.origin === target.origin &&
            current.pathname === target.pathname &&
            current.search === target.search
        ) {
            window.location.reload();
            return;
        }
    } catch (_error) {
        // Fall back to regular redirect when URL parsing is not possible.
    }

    window.location.href = targetUrl;
}

export function getBackUrlParameterFromWindow(): string {
    let backUrl = '';
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('backUrl')) {
        backUrl = new URL(searchParams.get('backUrl')).toString();
    }

    return backUrl;
}
